import React, { useEffect, useState, useRef } from 'react';
import './FeatureScrollSection.css';

const sections = [
    {
        id: 'predict-bbb',
        menuTitle: 'PredictBBB',
        title: 'PredictBBB',
        subtitle: 'Predict any molecule’s brain permeability',
        description: 'Achieves 94% accuracy with real-time machine learning, providing open-access to critical CNS drug development technology',
        bgColor: '#ffffff', // Placeholder for potential bg changes
        imageAlt: 'PredictBBB Interface',
        imageSrc: '/images/predictbbb.jpg',
    },
    {
        id: 'with-zeta',
        menuTitle: 'withZeta',
        title: 'withZeta',
        subtitle: 'AI-powered co-scientist for rare cancer research',
        description: 'With withZeta, you can turn fragmented clinical, molecular, and therapeutic data into evidence-grounded insights in minutes.',
        imageAlt: 'withZeta Interface',
        imageSrc: '/images/withzeta.jpg',
    },
    {
        id: 'lbx-ai',
        menuTitle: 'LBx-AI',
        title: 'LBx-AI',
        subtitle: 'Transform liquid biopsy data into predictive cancer insights',
        description: 'Transforming liquid biopsy data into actionable insights with 86% accuracy in predicting treatment response for non-small cell lung cancer patients.',
        imageAlt: 'LBx-AI Interface',
        imageSrc: '/images/liquidbiopsy.png',
    },
    {
        id: 'adc-module',
        menuTitle: 'ADC Module',
        title: 'ADC Module',
        subtitle: 'Accelerate and de-risk ADC development with AI-driven precision',
        description: 'Identifies high-value ADC targets and optimizes potent payloads, cutting development time and cost by up to 50%',
        imageAlt: 'ADC Module Interface',
        imageSrc: '/images/adcmodule.png',
    },
    {
        id: 'drug-combination',
        menuTitle: 'Drug Combination',
        title: 'Drug Combination Module',
        subtitle: 'Predict the synergy and safety of complex cancer drug combinations',
        description: 'Trained on 221 trials, Combination-AI forecasts efficacy and biomarker-driven response for DDA + DDR inhibitor regimens',
        imageAlt: 'Drug Combination Module Interface',
        imageSrc: '/images/drugcombination.png',
    }
];

const FeatureScrollSection = ({ onLearnMore }) => {
    const [activeSection, setActiveSection] = useState(sections[0].id);
    const sectionRefs = useRef({});

    const handleScrollTo = (id) => {
        const element = sectionRefs.current[id];
        if (element) {
            const yOffset = -200; // Adjust this value to account for header height or desired margin
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const callback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // When a section comes into view (specifically the center of the viewport)
                    setActiveSection(entry.target.id);
                }
            });
        };

        const options = {
            root: null,
            rootMargin: '-40% 0px -40% 0px', // Active when element is in the middle 20% of screen
            threshold: 0,
        };

        const observer = new IntersectionObserver(callback, options);

        sections.forEach(section => {
            const el = sectionRefs.current[section.id];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="feature-scroll-container">
            <div className="feature-content-wrapper">
                {/* Sticky Left Sidebar */}
                <div className="feature-sidebar">
                    <div className="sticky-menu">
                        {sections.filter(s => s.menuTitle).map((section) => (
                            <div
                                key={section.id}
                                className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                                onClick={() => handleScrollTo(section.id)}
                            >
                                <span className="sidebar-text">
                                    {section.menuTitle === 'PredictBBB' || section.menuTitle === 'withZeta' ? (
                                        <>
                                            {section.menuTitle}<sup className="tm-symbol">TM</sup>
                                        </>
                                    ) : (
                                        section.menuTitle
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Scrollable Content */}
                <div className="feature-cards-container">
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            id={section.id}
                            ref={el => sectionRefs.current[section.id] = el}
                            className="feature-card"
                        >
                            <div className="card-image-wrapper">
                                <img
                                    src={section.imageSrc}
                                    alt={section.imageAlt}
                                    className="feature-card-img"
                                />
                            </div>
                            <div className="card-content">
                                <h3 className="card-title">
                                    {section.title === 'PredictBBB' || section.title === 'withZeta' ? (
                                        <>
                                            {section.title}<sup className="tm-symbol">TM</sup>
                                        </>
                                    ) : (
                                        section.title
                                    )}
                                </h3>
                                <h4 className="card-subtitle">{section.subtitle}</h4>
                                <p className="card-description">{section.description}</p>
                                <button
                                    className="learn-more-btn"
                                    onClick={() => onLearnMore(section)}
                                >
                                    Learn more
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FeatureScrollSection;
